<template>
  <div>
    <a-tooltip :title="arrearsTooltip">
      <a-badge
        :count="hasPastYearsArrears ? pastYearsArrears.feeCount : 0"
        :number-style="{ backgroundColor: '#faad14' }"
      >
        <a-button
          :class="profile ? '' : 'm-2'"
          :loading="loadingPaymentStatus"
          :disabled="loadingPaymentStatus"
          @click="openPaymentStatusModal"
          :type="profile ? 'primary' : ''"
        >
          <a-icon
            :type="hasPastYearsArrears ? 'exclamation-circle' : 'credit-card'"
            class="mr-2"
          />
          {{ $t("paymentStatus.paymentStatus") }}
        </a-button>
      </a-badge>
    </a-tooltip>

    <a-modal
      :dialog-style="{ top: '20px' }"
      :title="modalTitle"
      :visible="visiblePaymentStatus"
      width="80%"
      :footer="null"
      @cancel="() => (visiblePaymentStatus = false)"
    >
      <!-- Fees still owed for the past school years -->
      <PastYearsArrearsAlert
        :parent-id="currentParent"
        :arrears="pastYearsArrears"
      />

      <!-- Header Section -->
      <div class="mb-4">
        <div class="row align-items-center">
          <div class="col-md-6">
            <label class="form-label fw-Bold mb-2">
              <a-icon type="team" class="mr-2" />
              {{ $t("menu.listeEleves") }}
            </label>
            <a-select
              :allowClear="false"
              mode="multiple"
              :placeholder="$t('menu.listeEleves')"
              style="width: 100%"
              option-filter-prop="children"
              v-model="selectedStudents"
              class="w-100"
            >
              <a-select-option
                v-for="student in paymentStatus"
                :key="student._id"
                :value="student._id"
              >
                {{ student.fullName }}
              </a-select-option>
            </a-select>
          </div>
          <div class="col-md-6 text-end">
            <a-button
              type="primary"
              size="large"
              :loading="loadingPaymentStatusPdf"
              :disabled="loadingPaymentStatusPdf"
              @click="printPaymentStatus"
              class="px-4"
            >
              <a-icon type="printer" class="mr-2" />
              {{ $t("action.imprimer") }}
            </a-button>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-4">
          <div class="card border-success bg-light-success">
            <div class="card-body text-center">
              <div
                class="d-flex align-items-center justify-content-center mb-2"
              >
                <a-icon
                  type="check-circle"
                  class="text-success mr-2"
                  style="font-size: 24px"
                />
                <h5 class="mb-0 text-success">
                  {{ $t("paymentStatus.paidAmount") }}
                </h5>
              </div>
              <h3 class="text-success mb-0">
                {{ totalPaidAmount.toFixed(2) }} TND
              </h3>
              <small class="text-muted"
                >{{ paidStatus.length }}
                {{ $t("financialStats.categories") }}</small
              >
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card border-warning bg-light-warning">
            <div class="card-body text-center">
              <div
                class="d-flex align-items-center justify-content-center mb-2"
              >
                <a-icon
                  type="clock-circle"
                  class="text-warning mr-2"
                  style="font-size: 24px"
                />
                <h5 class="mb-0 text-warning">
                  {{
                    $t("paymentStatus.monthRemainingPayments", {
                      month: currentMonthName,
                    })
                  }}
                </h5>
              </div>
              <h3 class="text-warning mb-0">
                {{ totalCurrentMonthAmount.toFixed(2) }} TND
              </h3>
              <small class="text-muted"
                >{{ currentMonthPay.length }}
                {{ $t("financialStats.categories") }}</small
              >
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card border-danger bg-light-danger">
            <div class="card-body text-center">
              <div
                class="d-flex align-items-center justify-content-center mb-2"
              >
                <a-icon
                  type="exclamation-circle"
                  class="text-danger mr-2"
                  style="font-size: 24px"
                />
                <h5 class="mb-0 text-danger">
                  {{ $t("paymentStatus.allRemainingPayments") }}
                </h5>
              </div>
              <h3 class="text-danger mb-0">
                {{ totalRemainingAmount.toFixed(2) }} TND
              </h3>
              <small class="text-muted"
                >{{ resetToPay.length }}
                {{ $t("financialStats.categories") }}</small
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Status Tabs -->
      <a-tabs v-model="activeTabKey" type="card" class="payment-status-tabs">
        <!-- Paid Payments Tab -->
        <a-tab-pane key="paid" :tab="paidTabTitle">
          <div v-if="paidStatus.length > 0" class="mt-3">
            <div class="row">
              <div
                v-for="(payment, index) in paidStatus"
                :key="payment.name"
                class="col-lg-6 col-md-12 mb-3"
              >
                <div class="card h-100 border-success">
                  <div
                    class="card-header bg-success text-white d-flex justify-content-between align-items-center"
                  >
                    <h6 class="mb-0">
                      <a-icon type="check-circle" class="mr-2" />
                      {{ payment.name }}
                    </h6>
                    <div class="d-flex align-items-center">
                      <span class="badge bg-light text-success me-2">
                        {{ payment.money.toFixed(2) }} TND
                      </span>
                      <a-tag color="green" v-if="payment.completed">
                        {{ $t("profil.payes") }}
                      </a-tag>
                    </div>
                  </div>
                  <div class="card-body">
                    <div
                      v-for="student in payment.students"
                      :key="student.fullName"
                      class="mb-3"
                    >
                      <h6 class="text-primary mb-2">
                        <a-icon type="user" class="mr-2" />
                        {{ student.fullName }}
                      </h6>
                      <div v-if="payment.daily" class="mb-2">
                        <DailyCalendar
                          :show-selected-dates="false"
                          :selected-dates="buildUnpaidDates(student.details, false)"
                          :activity-start="null"
                          :activity-end="null"
                          :payment-data="buildCalendarPaymentData(student.details)"
                          :compact="true"
                        />
                        <div class="mt-2">
                          <div class="d-flex align-items-center mb-1">
                            <h6 class="mb-0">{{ $t('inscription.frais') }}</h6>
                          </div>
                          <div class="d-flex flex-wrap" style="gap:8px;">
                            <a-tag
                              v-for="detail in student.details"
                              :key="detail.feeName + '-paid'"
                              :color="getFeeTagColorFromDetail(detail)"
                            >
                              {{ formatFeeName(detail.feeName, payment.daily) }}
                            </a-tag>
                          </div>
                        </div>
                      </div>
                      <div v-else class="row">
                        <div
                          v-for="detail in student.details"
                          :key="detail.feeName"
                          class="col-12 mb-2"
                        >
                          <div
                            class="d-flex justify-content-between align-items-center p-2 bg-light rounded"
                          >
                            <span class="fw-medium">{{
                              formatFeeName(detail.feeName, payment.daily)
                            }}</span>
                            <div class="d-flex align-items-center">
                              <span class="text-success fw-Bold me-2"
                                >{{ detail.prix }} TND</span
                              >
                              <a-tag color="green" size="small">{{
                                $t("profil.payes")
                              }}</a-tag>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-5">
            <a-empty
              :description="$t('paymentStatus.nothingPaid')"
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            />
          </div>
        </a-tab-pane>

        <!-- Current Month Payments Tab -->
        <a-tab-pane key="currentMonth" :tab="currentMonthTabTitle">
          <div v-if="currentMonthPay.length > 0" class="mt-3">
            <div class="row">
              <div
                v-for="(payment, index) in currentMonthPay"
                :key="payment.name"
                class="col-lg-6 col-md-12 mb-3"
              >
                <div class="card h-100 border-warning">
                  <div
                    class="card-header bg-warning text-dark d-flex justify-content-between align-items-center"
                  >
                    <h6 class="mb-0">
                      <a-icon type="clock-circle" class="mr-2" />
                      {{ payment.name }}
                    </h6>
                    <span class="badge bg-light text-warning">
                      {{ payment.money.toFixed(2) }} TND
                    </span>
                  </div>
                  <div class="card-body">
                    <div
                      v-for="student in payment.students"
                      :key="student.fullName"
                      class="mb-3"
                    >
                      <h6 class="text-primary mb-2">
                        <a-icon type="user" class="mr-2" />
                        {{ student.fullName }}
                      </h6>
                      <div v-if="payment.daily" class="mb-2">
                        <DailyCalendar
                          :show-selected-dates="false"
                          :selected-dates="buildUnpaidDates(student.details, true)"
                          :activity-start="null"
                          :activity-end="null"
                          :payment-data="buildCalendarPaymentData(student.details)"
                          :compact="true"
                        />
                        <div class="mt-2">
                          <div class="d-flex align-items-center mb-1">
                            <h6 class="mb-0">{{ $t('inscription.frais') }}</h6>
                          </div>
                          <div class="d-flex flex-wrap" style="gap:8px;">
                            <a-tag
                              v-for="detail in student.details"
                              :key="detail.feeName + '-curr'"
                              :color="getFeeTagColorFromDetail(detail)"
                            >
                              {{ formatFeeName(detail.feeName, payment.daily) }}
                            </a-tag>
                          </div>
                        </div>
                      </div>
                      <div v-else class="row">
                        <div
                          v-for="detail in student.details"
                          :key="detail.feeName"
                          class="col-12 mb-2"
                        >
                          <div
                            class="d-flex justify-content-between align-items-center p-2 bg-light rounded"
                          >
                            <span class="fw-medium">{{
                              formatFeeName(detail.feeName, payment.daily)
                            }}</span>
                            <div class="d-flex align-items-center">
                              <span class="text-warning fw-Bold me-2"
                                >{{ detail.prix }} TND</span
                              >
                              <a-tag color="orange" size="small">{{
                                $t("profil.nonPayes")
                              }}</a-tag>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-5">
            <a-empty
              :description="$t('paymentStatus.noRemainingPyaments')"
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            />
          </div>
        </a-tab-pane>

        <!-- All Remaining Payments Tab -->
        <a-tab-pane key="remaining" :tab="remainingTabTitle">
          <div v-if="resetToPay.length > 0" class="mt-3">
            <div class="row">
              <div
                v-for="(payment, index) in resetToPay"
                :key="payment.name"
                class="col-lg-6 col-md-12 mb-3"
              >
                <div class="card h-100 border-danger">
                  <div
                    class="card-header bg-danger text-white d-flex justify-content-between align-items-center"
                  >
                    <h6 class="mb-0">
                      <a-icon type="exclamation-circle" class="mr-2" />
                      {{ payment.name }}
                    </h6>
                    <span class="badge bg-light text-danger">
                      {{ payment.money.toFixed(2) }} TND
                    </span>
                  </div>
                  <div class="card-body">
                    <div
                      v-for="student in payment.students"
                      :key="student.fullName"
                      class="mb-3"
                    >
                      <h6 class="text-primary mb-2">
                        <a-icon type="user" class="mr-2" />
                        {{ student.fullName }}
                      </h6>
                      <div v-if="payment.daily" class="mb-2">
                        <DailyCalendar
                          :show-selected-dates="false"
                          :selected-dates="buildUnpaidDates(student.details, true)"
                          :activity-start="null"
                          :activity-end="null"
                          :payment-data="buildCalendarPaymentData(student.details)"
                          :compact="true"
                        />
                        <div class="mt-2">
                          <div class="d-flex align-items-center mb-1">
                            <h6 class="mb-0">{{ $t('inscription.frais') }}</h6>
                          </div>
                          <div class="d-flex flex-wrap" style="gap:8px;">
                            <a-tag
                              v-for="detail in student.details"
                              :key="detail.feeName + '-rem'"
                              :color="getFeeTagColorFromDetail(detail)"
                            >
                              {{ formatFeeName(detail.feeName, payment.daily) }}
                            </a-tag>
                          </div>
                        </div>
                      </div>
                      <div v-else class="row">
                        <div
                          v-for="detail in student.details"
                          :key="detail.feeName"
                          class="col-12 mb-2"
                        >
                          <div
                            class="d-flex justify-content-between align-items-center p-2 bg-light rounded"
                          >
                            <span class="fw-medium">{{
                              formatFeeName(detail.feeName, payment.daily)
                            }}</span>
                            <div class="d-flex align-items-center">
                              <span class="text-danger fw-Bold me-2"
                                >{{ detail.prix }} TND</span
                              >
                              <a-tag color="red" size="small">{{
                                $t("profil.nonPayes")
                              }}</a-tag>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-5">
            <a-empty
              :description="$t('paymentStatus.noRemainingPyaments')"
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            />
          </div>
        </a-tab-pane>
      </a-tabs>
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */

import { mapState, mapActions, mapGetters } from "vuex";
import apiClient from "@/services/axios";
import moment from "moment";
import "jspdf-autotable";
import { formatFeeName } from "@/utilities/settings";
import { pdfHeader } from "@/utilities/pdfUtils";
import DailyCalendar from "@/components/comptabilite/DailyCalendar.vue";
import PastYearsArrearsAlert from "@/components/status/PastYearsArrearsAlert.vue";
import { fetchPastYearsArrears } from "@/services/pastYearsArrears";

export default {
  props: {
    currentParent: {
      type: String,
      required: true,
    },
    profile: {
      type: Boolean,
      default: false,
    },
    selectedStudent: {
      type: String,
      default: null,
    },
  },
  components: { DailyCalendar, PastYearsArrearsAlert },

  data: function () {
    return {
      visiblePaymentStatus: false,
      loadingPaymentStatus: false,
      loadingPaymentStatusPdf: false,
      selectedStudents: [],
      moment,
      paymentStatus: [],
      currentMonthName: moment().format("MMMM"),
      activeTabKey: "paid", // Default active tab
      pastYearsArrears: null,
    };
  },

  created() {
    this.loadPastYearsArrears();
  },

  watch: {
    currentParent() {
      this.loadPastYearsArrears();
    },
  },
  computed: {
    ...mapState(["user", "settings"]),
    ...mapGetters("settings", ["btnAccessGetter"]),

    hasPastYearsArrears() {
      return Boolean(this.pastYearsArrears && this.pastYearsArrears.hasArrears);
    },

    // Empty title keeps a-tooltip from rendering, so no popup without arrears.
    arrearsTooltip() {
      if (!this.hasPastYearsArrears) return "";
      const { years, total } = this.pastYearsArrears;
      const yearNames = years.map((y) => y.schoolarYear.name).join(", ");
      return `${this.$t(
        "paymentStatus.pastYearsArrearsTitle"
      )} (${yearNames}) : ${Number(total).toFixed(2)} TND`;
    },

    modalTitle() {
      const studentCount = this.selectedStudents.length;
      return `${this.$t(
        "paymentStatus.paymentStatus"
      )} - ${studentCount} élève${studentCount > 1 ? "s" : ""}`;
    },

    paidTabTitle() {
      const count = this.paidStatus.length;
      return `${this.$t("paymentStatus.paidAmount")} (${count})`;
    },

    currentMonthTabTitle() {
      const count = this.currentMonthPay.length;
      return `${this.$t("paymentStatus.monthRemainingPayments", {
        month: this.currentMonthName,
      })} (${count})`;
    },

    remainingTabTitle() {
      const count = this.resetToPay.length;
      return `${this.$t("paymentStatus.allRemainingPayments")} (${count})`;
    },

    totalPaidAmount() {
      return this.paidStatus.reduce(
        (total, payment) => total + Number(payment.money),
        0
      );
    },

    totalCurrentMonthAmount() {
      return this.currentMonthPay.reduce(
        (total, payment) => total + Number(payment.money),
        0
      );
    },

    totalRemainingAmount() {
      return this.resetToPay.reduce(
        (total, payment) => total + Number(payment.money),
        0
      );
    },

    paidStatus() {
      let paidStatus = [];
      this.selectedStudents.forEach((studentID) => {
        const student = this.paymentStatus.find((s) => s._id == studentID);
        if (student) {
          student.studentStatus.paid.map((p) => {
            const payement = paidStatus.find((ps) => ps.name == p.name);
            if (!payement)
              paidStatus.push({
                money: Number(p.money),
                name: p.name,
                daily: p.daily,
                students: [
                  {
                    completed: p.completed,
                    details: p.details,
                    fullName: student.fullName,
                  },
                ],
              });
            else {
              payement.money += Number(p.money);
              payement.students.push({
                daily: p.daily,
                details: p.details,
                fullName: student.fullName,
              });
            }
          });
        }

        paidStatus.map((p) => {
          p.completed = p.students.every((s) => s.completed);
          p.money.toFixed(2);
        });
      });

      return paidStatus;
    },
    currentMonthPay() {
      let currentMonthPay = [];
      this.selectedStudents.forEach((studentID) => {
        const student = this.paymentStatus.find((s) => s._id == studentID);
        if (student) {
          student.studentStatus.currentMonthPay.map((p) => {
            const payment = currentMonthPay.find((ps) => ps.name == p.name);
            if (!payment) {
              currentMonthPay.push({
                money: Number(p.money),
                name: p.name,
                daily: p.daily,
                students: [
                  {
                    details: p.details,
                    fullName: student.fullName,
                  },
                ],
              });
            } else {
              payment.money += Number(p.money);
              payment.students.push({
                daily: p.daily,
                details: p.details,
                fullName: student.fullName,
              });
            }
          });
        }
        currentMonthPay.map((p) => {
          p.money.toFixed(2);
        });
      });

      return currentMonthPay;
    },
    resetToPay() {
      let resetToPay = [];
      this.selectedStudents.forEach((studentID) => {
        const student = this.paymentStatus.find((s) => s._id == studentID);
        if (student) {
          student.studentStatus.resetToPay.map((p) => {
            const payment = resetToPay.find((ps) => ps.name == p.name);
            if (!payment) {
              resetToPay.push({
                money: Number(p.money),
                name: p.name,
                daily: p.daily,
                students: [
                  {
                    details: p.details,
                    fullName: student.fullName,
                  },
                ],
              });
            } else {
              payment.money += Number(p.money);
              payment.students.push({
                daily: p.daily,
                details: p.details,
                fullName: student.fullName,
              });
            }
          });
        }
        resetToPay.map((p) => {
          p.money.toFixed(2);
        });
      });

      return resetToPay;
    },
  },
  methods: {
    ...mapActions("settings", ["packHasBtnAccess"]),

    // Loaded upfront so the button can flag arrears before it is even opened.
    async loadPastYearsArrears() {
      if (!this.currentParent) {
        this.pastYearsArrears = null;
        return;
      }
      this.pastYearsArrears = await fetchPastYearsArrears({
        parentId: this.currentParent,
      });
    },

    formatFeeName(text, daily) {

      return formatFeeName(text, daily, this.$t.bind(this));
    },
    buildCalendarPaymentData(details) {
      try {
        if (!Array.isArray(details)) return [];
        return details
          .filter((d) => d && typeof d.feeName === "string" && d.feeName !== "inscription")
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.feeName))
          .map((d) => {
            const fullyPaid = d?.isPayed?.fullyPaid === true || d?.paid === true;
            const remaining = d?.isPayed?.remaining ?? d?.remaining ?? 0;
            return {
              date: d.feeName,
              paid: fullyPaid,
              remaining: Number(remaining) || 0,
              isPayed: {
                fullyPaid: !!fullyPaid,
                paid: Number(d?.isPayed?.paid ?? d?.paid ?? 0) || 0,
                remaining: Number(remaining) || 0,
              },
            };
          });
      } catch (e) {
        return [];
      }
    },
    async openPaymentStatusModal() {
      if (!(await this.packHasBtnAccess("paymentStatus"))) return;
      if (!this.currentParent) {
        this.$message.error("Parent ID is required");
        return;
      }

      this.loadingPaymentStatus = true;
      try {
        const res = await apiClient.get(
          "/paymentStatus/parent/" + this.currentParent
        );
        this.paymentStatus = res.data;
        if (!this.selectedStudent)
          this.selectedStudents = res.data.map((e) => e._id);
        else this.selectedStudents = [this.selectedStudent];

        this.visiblePaymentStatus = true;
      } catch (error) {
        console.error("Error fetching payment status:", error);
        this.$message.error("Error loading payment status");
      } finally {
        this.loadingPaymentStatus = false;
      }
    },
    printPaymentStatus() {
      this.$gtag.event("Imp PaymentStatus", {
        event_category: "Impression PDF",
        event_label: "Profile eleve section",
        value: 1,
      });

      this.loadingPaymentStatusPdf = true;
      try {
        // Filter selected students and deduplicate by _id
        const seen = new Set();
        let data = this.paymentStatus.filter((s) => {
          if (!this.selectedStudents.includes(s._id) || seen.has(s._id)) return false;
          seen.add(s._id);
          return true;
        });

        const isArabic = this.$i18n.locale === 'ar-AR';

        // Use landscape for better table layout
        const doc = pdfHeader(this.settings, {
          title: this.$t("paymentStatus.statutPaiement"),
          portrait: false,
          locale: this.$i18n.locale
        });

        const pageWidth = doc.internal.pageSize.getWidth();

        // Colors for payment status
        const COLORS = {
          paid: [144, 238, 144],        // Light green
          paidText: [14, 115, 31],      // Dark green
          partial: [255, 235, 156],     // Light yellow
          partialText: [184, 134, 11],  // Dark yellow/orange
          unpaid: [255, 182, 182],      // Light red
          unpaidText: [209, 23, 45],    // Dark red
          header: [240, 240, 240],      // Light gray (no purple)
          headerText: [0, 0, 0],        // Black text
          white: [255, 255, 255],
          black: [0, 0, 0],
          border: [0, 0, 0],            // Black border
          lightGray: [245, 245, 245],   // Very light gray
        };

        // Helper to draw cell with black border
        const drawCell = (x, y, w, h, fillColor = null, strokeOnly = false) => {
          doc.setDrawColor(...COLORS.border);
          doc.setLineWidth(0.3);
          if (fillColor && !strokeOnly) {
            doc.setFillColor(...fillColor);
            doc.rect(x, y, w, h, 'FD'); // Fill and Draw border
          } else {
            doc.rect(x, y, w, h, 'S'); // Stroke only
          }
        };

        // Helper to get payment status and color
        const getPaymentStatus = (detail) => {
          if (!detail) return { status: 'unpaid', color: COLORS.unpaid, textColor: COLORS.unpaidText };

          const isPayed = detail.isPayed;

          // Check if fully paid
          if (isPayed?.fullyPaid || detail.paid === true) {
            return { status: 'paid', color: COLORS.paid, textColor: COLORS.paidText };
          }

          // Get paid and remaining amounts from our merged data structure
          const paidAmount = detail.paidAmount || isPayed?.paid || 0;
          const remainingAmount = detail.remainingAmount || isPayed?.remaining || 0;

          // Partial: has some payment but still has remaining
          if (Number(paidAmount) > 0 && Number(remainingAmount) > 0) {
            return { status: 'partial', color: COLORS.partial, textColor: COLORS.partialText };
          }

          // Fully paid if has payment and no remaining
          if (Number(paidAmount) > 0 && Number(remainingAmount) === 0) {
            return { status: 'paid', color: COLORS.paid, textColor: COLORS.paidText };
          }

          return { status: 'unpaid', color: COLORS.unpaid, textColor: COLORS.unpaidText };
        };

        // Process each student
        let currentY = 32;
        let globalTotalToPay = 0;
        let globalTotalPaid = 0;
        let globalRemaining = 0;

        data.forEach((student, studentIndex) => {
          // Combine all fees from paid, currentMonthPay, and resetToPay
          const allFees = new Map();

          // Process paid fees
          student.studentStatus.paid.forEach((category) => {
            if (!allFees.has(category.name)) {
              allFees.set(category.name, {
                name: category.name,
                daily: category.daily,
                mode: category.mode,
                details: new Map(),
              });
            }
            const feeData = allFees.get(category.name);
            category.details.forEach((detail) => {
              feeData.details.set(detail.feeName, {
                ...detail,
                paidAmount: Number(detail.prix) || 0,
              });
            });
          });

          // Process resetToPay (remaining fees)
          student.studentStatus.resetToPay.forEach((category) => {
            if (!allFees.has(category.name)) {
              allFees.set(category.name, {
                name: category.name,
                daily: category.daily,
                mode: category.mode,
                details: new Map(),
              });
            }
            const feeData = allFees.get(category.name);
            category.details.forEach((detail) => {
              const existing = feeData.details.get(detail.feeName);
              if (existing) {
                existing.remainingAmount = Number(detail.prix) || 0;
                existing.isPayed = detail.isPayed;
              } else {
                feeData.details.set(detail.feeName, {
                  ...detail,
                  remainingAmount: Number(detail.prix) || 0,
                  paidAmount: 0,
                });
              }
            });
          });

          // Convert to array and process
          const feesArray = Array.from(allFees.values());

          if (feesArray.length === 0) return;

          // Check if we need a new page
          if (currentY > doc.internal.pageSize.getHeight() - 60) {
            doc.addPage();
            currentY = 20;
          }

          // Student header
          drawCell(10, currentY, pageWidth - 20, 8, COLORS.lightGray);
          doc.setTextColor(...COLORS.black);
          doc.setFontSize(11);
          doc.setFont("Amiri", "Bold");
          doc.text(
            `${this.$t("paymentStatus.nomEleve")}: ${student.fullName}`,
            pageWidth / 2,
            currentY + 5.5,
            { align: 'center' }
          );
          currentY += 10;

          // Summary headers
          const summaryHeaders = [
            this.$t("paymentStatus.totalAPayer") || "Total à payer",
            this.$t("paymentStatus.totalPaye") || "Total payé",
            this.$t("paymentStatus.resteAPayer") || "Reste à payer"
          ];

          // Process each fee category for this student
          let studentTotalToPay = 0;
          let studentTotalPaid = 0;
          let studentRemaining = 0;

          feesArray.forEach((fee) => {
            // Check if we need a new page
            if (currentY > doc.internal.pageSize.getHeight() - 50) {
              doc.addPage();
              currentY = 20;
            }

            const detailsArray = Array.from(fee.details.values())
              .filter(d => d.feeName !== 'inscription')
              .sort((a, b) => {
                // Sort by date for daily fees, or by month name
                if (fee.daily) {
                  return new Date(a.feeName) - new Date(b.feeName);
                }
                return a.feeName.localeCompare(b.feeName);
              });

            // Add inscription at the beginning if it exists
            const inscription = fee.details.get('inscription');
            if (inscription) {
              detailsArray.unshift(inscription);
            }

            if (detailsArray.length === 0) return;

            // Calculate totals for this fee
            let feeTotalToPay = 0;
            let feeTotalPaid = 0;

            detailsArray.forEach((detail) => {
              const paidAmt = detail.paidAmount || (detail.isPayed?.paid) || 0;
              const remainingAmt = detail.remainingAmount || (detail.isPayed?.remaining) || 0;
              feeTotalPaid += Number(paidAmt);
              feeTotalToPay += Number(paidAmt) + Number(remainingAmt);
            });

            const feeRemaining = feeTotalToPay - feeTotalPaid;

            studentTotalToPay += feeTotalToPay;
            studentTotalPaid += feeTotalPaid;
            studentRemaining += feeRemaining;

            // Determine column layout - all columns in one row
            const summaryColWidth = 25;
            const labelColWidth = 35;
            const availableWidth = pageWidth - 20 - (summaryColWidth * 3) - labelColWidth;
            const totalCols = detailsArray.length;
            const colWidth = totalCols > 0 ? availableWidth / totalCols : 18;
            // Use abbreviated month names (Jan, Feb...) when many columns
            const useShortMonths = totalCols > 10;
            // Smaller font when many columns
            const dateFontSize = totalCols > 14 ? 5 : totalCols > 9 ? 6 : 7;
            const amountFontSize = totalCols > 14 ? 5 : totalCols > 9 ? 6 : 7;

            const feeNameWidth = totalCols * colWidth;

            // Summary headers ordered for RTL/LTR
            const summaryHeadersOrdered = isArabic ? [...summaryHeaders].reverse() : summaryHeaders;
            const summaryValues = [
              { value: feeTotalToPay.toFixed(0), color: COLORS.white, textColor: COLORS.black },
              { value: feeTotalPaid.toFixed(0), color: COLORS.paid, textColor: COLORS.paidText },
              { value: feeRemaining.toFixed(0), color: COLORS.unpaid, textColor: COLORS.unpaidText }
            ];
            const orderedSummaryValues = isArabic ? [...summaryValues].reverse() : summaryValues;

            // Positions depend on RTL/LTR
            let labelStartX, feeStartX, summaryStartX;
            if (isArabic) {
              summaryStartX = 10;
              feeStartX = summaryStartX + (summaryColWidth * 3);
              labelStartX = feeStartX + feeNameWidth;
            } else {
              labelStartX = 10;
              feeStartX = labelStartX + labelColWidth;
              summaryStartX = feeStartX + feeNameWidth;
            }

            // Row 1: "Frais" label + Fee name (centered) spanning columns
            drawCell(labelStartX, currentY, labelColWidth, 7, COLORS.lightGray);
            doc.setTextColor(...COLORS.black);
            doc.setFontSize(9);
            doc.setFont("Amiri", "Bold");
            doc.text(this.$t("paymentStatus.frais") || "Frais", labelStartX + labelColWidth / 2, currentY + 5, { align: 'center' });

            drawCell(feeStartX, currentY, feeNameWidth, 7, COLORS.white);
            doc.setTextColor(...COLORS.black);
            doc.setFontSize(9);
            doc.setFont("Amiri", "Bold");
            doc.text(fee.name, feeStartX + feeNameWidth / 2, currentY + 5, { align: 'center' });

            // Empty summary header cells for first row
            summaryHeadersOrdered.forEach((header, idx) => {
              drawCell(summaryStartX + (idx * summaryColWidth), currentY, summaryColWidth, 7, COLORS.lightGray);
            });

            currentY += 7;

            // Row 2: "Date" row with date labels
            drawCell(labelStartX, currentY, labelColWidth, 7, COLORS.lightGray);
            doc.setTextColor(...COLORS.black);
            doc.setFontSize(8);
            doc.setFont("Amiri", "Bold");
            doc.text(this.$t("paymentStatus.date"), labelStartX + labelColWidth / 2, currentY + 5, { align: 'center' });

            // Date cells - reverse order for RTL
            const orderedDetails = isArabic ? [...detailsArray].reverse() : detailsArray;
            let xPos = feeStartX;
            orderedDetails.forEach((detail) => {
              drawCell(xPos, currentY, colWidth, 7, COLORS.white);
              doc.setTextColor(...COLORS.black);
              doc.setFontSize(dateFontSize);
              doc.setFont("Amiri", "normal");

              let dateLabel;
              if (detail.feeName === 'inscription' || detail.feeName.toLowerCase() === 'inscription') {
                dateLabel = this.$t("paymentStatus.inscription");
              } else if (useShortMonths && moment(detail.feeName, moment.ISO_8601, true).isValid()) {
                // Abbreviated month name: Jan, Fév, Mar, etc.
                const d = moment(detail.feeName);
                if (d.format("DD") === "01" && !fee.daily) {
                  dateLabel = d.format("MMM");
                  dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
                } else {
                  dateLabel = d.format("DD/MM");
                }
              } else {
                dateLabel = this.formatFeeName(detail.feeName, fee.daily);
                dateLabel = dateLabel.replace(/(\d{2}\/\d{2})\/\d{4}(\s*\([^)]*\))?/, '$1');
                if (dateLabel.toLowerCase() === 'inscription') {
                  dateLabel = this.$t("paymentStatus.inscription");
                }
              }
              doc.text(dateLabel, xPos + colWidth / 2, currentY + 5, { align: 'center' });
              xPos += colWidth;
            });

            // Summary column headers
            summaryHeadersOrdered.forEach((header, idx) => {
              drawCell(summaryStartX + (idx * summaryColWidth), currentY, summaryColWidth, 7, COLORS.lightGray);
              doc.setTextColor(...COLORS.black);
              doc.setFontSize(7);
              doc.setFont("Amiri", "Bold");
              doc.text(header, summaryStartX + (idx * summaryColWidth) + summaryColWidth / 2, currentY + 5, { align: 'center' });
            });

            currentY += 7;

            // Row 3: "Montant" row with amounts (color-coded)
            drawCell(labelStartX, currentY, labelColWidth, 8, COLORS.lightGray);
            doc.setTextColor(...COLORS.black);
            doc.setFontSize(8);
            doc.setFont("Amiri", "Bold");
            doc.text(this.$t("paymentStatus.montant") || "Montant", labelStartX + labelColWidth / 2, currentY + 5.5, { align: 'center' });

            // Amount cells with color coding
            xPos = feeStartX;
            orderedDetails.forEach((detail) => {
              const status = getPaymentStatus(detail);
              drawCell(xPos, currentY, colWidth, 8, status.color);
              doc.setTextColor(...status.textColor);
              doc.setFontSize(amountFontSize);
              doc.setFont("Amiri", "Bold");

              const paidAmt = detail.paidAmount || (detail.isPayed?.paid) || 0;
              const remainingAmt = detail.remainingAmount || (detail.isPayed?.remaining) || 0;
              let displayAmount;
              if (status.status === 'paid') {
                displayAmount = Number(paidAmt).toFixed(0);
              } else if (status.status === 'partial') {
                displayAmount = Number(paidAmt).toFixed(0);
              } else {
                displayAmount = Number(remainingAmt).toFixed(0) || Number(detail.prix || 0).toFixed(0);
              }

              doc.text(displayAmount, xPos + colWidth / 2, currentY + 5.5, { align: 'center' });
              xPos += colWidth;
            });

            // Summary values with black borders
            orderedSummaryValues.forEach((item, idx) => {
              drawCell(summaryStartX + (idx * summaryColWidth), currentY, summaryColWidth, 8, item.color);
              doc.setTextColor(...item.textColor);
              doc.setFontSize(8);
              doc.setFont("Amiri", "Bold");
              doc.text(item.value, summaryStartX + (idx * summaryColWidth) + summaryColWidth / 2, currentY + 5.5, { align: 'center' });
            });

            currentY += 10;
          });

          // Student total row - RTL aware
          const totalRowWidth = pageWidth - 20 - (25 * 3);
          let studentTotalLabelX, studentSummaryStartX;

          if (isArabic) {
            // RTL: Summary on left, label on right
            studentSummaryStartX = 10;
            studentTotalLabelX = 10 + (25 * 3);
          } else {
            // LTR: Label on left, summary on right
            studentTotalLabelX = 10;
            studentSummaryStartX = pageWidth - 10 - (25 * 3);
          }

          drawCell(studentTotalLabelX, currentY, totalRowWidth, 8, COLORS.lightGray);
          doc.setTextColor(...COLORS.black);
          doc.setFontSize(9);
          doc.setFont("Amiri", "Bold");
          doc.text("Total", studentTotalLabelX + totalRowWidth / 2, currentY + 5.5, { align: 'center' });

          // Summary values for student with black borders - RTL aware
          const studentTotalValues = [
            { value: studentTotalToPay.toFixed(2), color: COLORS.white, textColor: COLORS.black },
            { value: studentTotalPaid.toFixed(2), color: COLORS.paid, textColor: COLORS.paidText },
            { value: studentRemaining.toFixed(2), color: COLORS.unpaid, textColor: COLORS.unpaidText }
          ];
          const orderedStudentTotalValues = isArabic ? [...studentTotalValues].reverse() : studentTotalValues;

          orderedStudentTotalValues.forEach((item, idx) => {
            drawCell(studentSummaryStartX + (idx * 25), currentY, 25, 8, item.color);
            doc.setTextColor(...item.textColor);
            doc.text(item.value, studentSummaryStartX + (idx * 25) + 12.5, currentY + 5.5, { align: 'center' });
          });

          currentY += 12;

          // Update global totals
          globalTotalToPay += studentTotalToPay;
          globalTotalPaid += studentTotalPaid;
          globalRemaining += studentRemaining;
        });

        // Global total row (if multiple students) - RTL aware
        if (data.length > 1) {
          // Check if we need a new page
          if (currentY > doc.internal.pageSize.getHeight() - 30) {
            doc.addPage();
            currentY = 20;
          }

          const globalTotalRowWidth = pageWidth - 20 - (25 * 3);
          let globalTotalLabelX, globalSummaryStartX;

          if (isArabic) {
            // RTL: Summary on left, label on right
            globalSummaryStartX = 10;
            globalTotalLabelX = 10 + (25 * 3);
          } else {
            // LTR: Label on left, summary on right
            globalTotalLabelX = 10;
            globalSummaryStartX = pageWidth - 10 - (25 * 3);
          }

          drawCell(globalTotalLabelX, currentY, globalTotalRowWidth, 10, COLORS.lightGray);
          doc.setTextColor(...COLORS.black);
          doc.setFontSize(11);
          doc.setFont("Amiri", "Bold");
          const globalTotalLabel = isArabic ? "المجموع الكلي" : "Total Général";
          doc.text(globalTotalLabel, globalTotalLabelX + globalTotalRowWidth / 2, currentY + 7, { align: 'center' });

          // Global summary values with black borders - RTL aware
          const globalTotalValues = [
            { value: globalTotalToPay.toFixed(2), color: COLORS.white, textColor: COLORS.black },
            { value: globalTotalPaid.toFixed(2), color: COLORS.paid, textColor: COLORS.paidText },
            { value: globalRemaining.toFixed(2), color: COLORS.unpaid, textColor: COLORS.unpaidText }
          ];
          const orderedGlobalTotalValues = isArabic ? [...globalTotalValues].reverse() : globalTotalValues;

          orderedGlobalTotalValues.forEach((item, idx) => {
            drawCell(globalSummaryStartX + (idx * 25), currentY, 25, 10, item.color);
            doc.setTextColor(...item.textColor);
            doc.setFontSize(10);
            doc.text(item.value, globalSummaryStartX + (idx * 25) + 12.5, currentY + 7, { align: 'center' });
          });

          currentY += 12;
        }

        // Color legend - only add new page if really necessary (legend needs ~12 units)
        currentY += 3;
        if (currentY > doc.internal.pageSize.getHeight() - 15) {
          doc.addPage();
          currentY = 20;
        }

        doc.setFontSize(9);
        doc.setFont("Amiri", "Bold");
        doc.setTextColor(...COLORS.black);
        doc.text(this.$t("paymentStatus.legende") + " :", 10, currentY);
        currentY += 6;

        const legendItems = [
          { color: COLORS.paid, text: this.$t("paiement.paye") || "Payé" },
          { color: COLORS.partial, text: this.$t("paiement.partiel") || "Partiellement payé" },
          { color: COLORS.unpaid, text: this.$t("paiement.impaye") || "Non payé" },
        ];

        let legendX = 10;
        legendItems.forEach((item) => {
          drawCell(legendX, currentY - 4, 10, 5, item.color);
          doc.setFontSize(8);
          doc.setFont("Amiri", "normal");
          doc.setTextColor(...COLORS.black);
          doc.text(item.text, legendX + 12, currentY);
          legendX += 55;
        });

        // Page numbering
        const pages = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);

        for (let j = 1; j < pages + 1; j++) {
          let horizontalPos = pageWidth / 2;
          let verticalPos = pageHeight - 10;
          doc.setPage(j);
          doc.text(
            this.$t("pdf.pageNumber", { current: j, total: pages }),
            horizontalPos,
            verticalPos,
            { align: "center" }
          );
        }

        this.loadingPaymentStatusPdf = false;
        doc.save(`${this.$t("paymentStatus.statutPaiement")}.pdf`);
      } catch (error) {
        console.log(error.stack);
      } finally {
        this.loadingPaymentStatusPdf = false;
      }
    },
    buildUnpaidDates(details, includeAll = false) {
      try {
        if (!Array.isArray(details)) return [];
        return details
          .filter((d) => d && typeof d.feeName === "string" && d.feeName !== "inscription")
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.feeName))
          .filter((d) => {
            if (includeAll) return true;
            const fullyPaid = d?.isPayed?.fullyPaid === true || d?.paid === true;
            const remaining = d?.isPayed?.remaining ?? d?.remaining ?? 0;
            // unpaid if not fully paid and remaining <= 0 means no payment tracked → still unpaid
            // or if there is a positive remaining (partial), keep out from unpaid list selection
            return !fullyPaid && !(remaining > 0);
          })
          .map((d) => d.feeName);
      } catch (e) {
        return [];
      }
    },

    getFeeTagColorFromDetail(detail) {
      if (detail?.isPayed?.fullyPaid || detail?.paid === true) return 'green';
      const remaining = detail?.isPayed?.remaining ?? detail?.remaining ?? 0;
      if (!detail?.isPayed?.fullyPaid && remaining > 0) return 'orange';
      return 'red';
    }
  },
};
</script>

<style scoped>
.payment-status-tabs .ant-tabs-tab {
  font-weight: 600;
  padding: 12px 24px;
}

.payment-status-tabs .ant-tabs-tab-active {
  background-color: #f0f8ff;
  border-color: #1890ff;
}

.card {
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.card-header {
  border-bottom: none;
  font-weight: 600;
}

.bg-light-success {
  background-color: #f6ffed;
}

.bg-light-warning {
  background-color: #fffbe6;
}

.bg-light-danger {
  background-color: #fff2f0;
}

.badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.form-label {
  font-size: 0.875rem;
  color: #495057;
}

.text-primary {
  color: #1890ff !important;
}

.fw-medium {
  font-weight: 500;
}

.fw-Bold {
  font-weight: 700;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .col-md-6 {
    margin-bottom: 1rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .card-header .badge {
    margin-top: 0.5rem;
  }
}
</style>
